import { useState, useEffect, useRef, Fragment, useMemo } from "react";
import CommonAPI from '~/api/commonApi';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCog } from "@fortawesome/free-solid-svg-icons";

function Table({ tableConfig }) {
    const {
        columns = [],
        data = [],
        tableKey = "Empty",
        hasShowHideColumn = true,
        extraButtons = []
    } = tableConfig;

    const [expandedCells, setExpandedCells] = useState({});
    const [visibleColumns, setVisibleColumns] = useState([]);
    const [tempColumns, setTempColumns] = useState([]);
    const [showAction, setShowAction] = useState(false);

    /* =========================================================
       ORDERED COLUMNS
       - Column[0] luôn đứng đầu
       - Action (key === "Action") là 1 group
    ========================================================= */
    const orderedColumns = useMemo(() => {
        if (!columns.length) return [];

        const firstCol = columns[0];
        const actionCols = [];
        const otherCols = [];

        for (let i = 1; i < columns.length; i++) {
            const col = columns[i];
            if (col.key === "Action") {
                actionCols.push(col);
            } else {
                otherCols.push(col);
            }
        }

        return showAction
            ? [firstCol, ...actionCols, ...otherCols]
            : [firstCol, ...otherCols, ...actionCols];
    }, [columns, showAction]);

    /* =========================================================
       CONFIG UI: gộp Action thành 1 checkbox
    ========================================================= */
    const renderableColumnsForConfig = useMemo(() => {
        const result = [];
        let actionAdded = false;

        orderedColumns.forEach(col => {
            if (col.key === "Action") {
                if (!actionAdded) {
                    result.push(col);
                    actionAdded = true;
                }
            } else {
                result.push(col);
            }
        });

        return result;
    }, [orderedColumns]);

    /* =========================================================
       LOAD COLUMN SETTING
    ========================================================= */
    useEffect(() => {
        CommonAPI.getColumnSetting(tableKey)
            .then(res => {
                let cols = [];

                if (res.data?.VisibleColumns) {
                    cols = JSON.parse(res.data.VisibleColumns || "[]");
                } else {
                    cols = columns.map(c => c.key);
                }

                setVisibleColumns(cols);
                setTempColumns(cols);
                setShowAction(!!res.data?.IsShowAction);
            })
            .catch(() => {});
    }, [tableKey, columns]);

    /* =========================================================
       TOGGLE TEMP COLUMN (GROUP BASED)
    ========================================================= */
    const toggleTempColumn = (key) => {
        setTempColumns(prev =>
            prev.includes(key)
                ? prev.filter(c => c !== key)
                : [...prev, key]
        );
    };

    const visibleColumnsRef = useRef([]);
    useEffect(() => {
        visibleColumnsRef.current = visibleColumns;
    }, [visibleColumns]);

    /* =========================================================
       SAVE COLUMN CONFIG
    ========================================================= */
    const handleSaveChangesColumnConfig = () => {
        const payload = {
            TableKey: tableKey,
            VisibleColumns: JSON.stringify(tempColumns),
            IsShowAction: showAction,
            Action: "ColumnSetting"
        };

        CommonAPI.insUpdColumnSetting(payload)
            .then(() => {
                setVisibleColumns([...tempColumns]);
                visibleColumnsRef.current = [...tempColumns];
            })
            .catch(() => {})
            .finally(() => {
                const el = document.getElementById("dropdownColumnConfig");
                const instance = window.bootstrap.Dropdown.getInstance(el);
                instance?.hide();
            });
    };

    /* =========================================================
       TOGGLE SHOW ACTION LEFT / RIGHT
    ========================================================= */
    const toggleShowAction = (checked) => {
        setShowAction(checked);

        CommonAPI.insUpdColumnSetting({
            TableKey: tableKey,
            IsShowAction: checked,
            VisibleColumns: '[]',
            Action: "ShowAction",
        }).catch(() => {});
    };

    /* =========================================================
       MORE / LESS CELL
    ========================================================= */
    const toggleCell = (rowIndex, colKey) => {
        const cellId = `${rowIndex}-${colKey}`;
        setExpandedCells(prev => ({
            ...prev,
            [cellId]: !prev[cellId]
        }));
    };

    /* =========================================================
       RESET TEMP WHEN DROPDOWN CLOSE
    ========================================================= */
    useEffect(() => {
        const el = document.getElementById("dropdownColumnConfig");
        if (!el) return;

        const handler = () => {
            setTempColumns(visibleColumnsRef.current);
        };

        el.addEventListener("hidden.bs.dropdown", handler);
        return () => el.removeEventListener("hidden.bs.dropdown", handler);
    }, []);

    /* =========================================================
       RENDER
    ========================================================= */
    return (
        <div>
            {/* SHOW ACTION LEFT */}
            <div className="form-check mb-1 pt-2 pb-2">
                <input
                    type="checkbox"
                    className="form-check-input cursor-pointer"
                    id="chk-show-action"
                    checked={showAction}
                    onChange={(e) => toggleShowAction(e.target.checked)}
                />
                <label className="form-check-label fs-9 fw-bold" htmlFor="chk-show-action">
                    Show action on left side
                </label>
            </div>

            {/* SHOW / HIDE COLUMN CONFIG */}
            {hasShowHideColumn && (
                <div className="dropdown mt-3 mb-5 d-flex align-items-center">
                    <a
                        href="#!"
                        data-bs-toggle="dropdown"
                        role="button"
                        id="dropdownColumnConfig"
                        data-bs-auto-close="outside"
                        className="text-primary d-inline-block fw-bold nav-link dropdown-toggle dropdown-caret-none fs-9 text-decoration-underline"
                    >
                        <FontAwesomeIcon icon={faCog} className="me-1" />
                        Show/Hide Column Config
                    </a>

                    {extraButtons.length > 0 && (
                        <Fragment>
                            {extraButtons.map((btn, idx) => (
                                <div key={idx}>{btn}</div>
                            ))}
                        </Fragment>
                    )}

                    <div
                        className="dropdown-menu navbar-dropdown-caret py-0 shadow border"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="card border-0" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                            <div className="card-header py-3 text-center">
                                <button
                                    onClick={handleSaveChangesColumnConfig}
                                    className="btn btn-bt-primary"
                                >
                                    Save Changes
                                </button>
                            </div>

                            <div className="card-body p-0">
                                <div className="px-3 py-2">
                                    <div className="row g-0" style={{ maxWidth: '380px' }}>
                                        {renderableColumnsForConfig.map((col, i) => (
                                            <div key={i} className="col-sm-6">
                                                <div className="form-check">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input cursor-pointer"
                                                        id={`chk-${col.key}-${i}`}
                                                        checked={tempColumns.includes(col.key)}
                                                        onChange={() => toggleTempColumn(col.key)}
                                                    />
                                                    <label
                                                        className="form-check-label fs-9 fw-semibold"
                                                        htmlFor={`chk-${col.key}-${i}`}
                                                    >
                                                        {col.key === "Action"
                                                            ? "Action"
                                                            : col.name
                                                                ?.toLowerCase()
                                                                .replace(/\b\w/g, c => c.toUpperCase())
                                                        }
                                                    </label>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* TABLE */}
            <div className="table-responsive scrollbar ms-n1 ps-1" style={{ minHeight: '250px' }}>
                <table className="table table-sm fs-9 mb-0">
                    <thead>
                    <tr>
                        {orderedColumns.map((col, index) =>
                            visibleColumns.includes(col.key) ? (
                                <th
                                    key={index}
                                    className={`align-middle ${col.headerClassName || ""} px-2 py-3`}
                                    style={{ minWidth: col.width || 'auto' }}
                                >
                                    {col.type === "checkbox" ? (
                                        <div className="form-check fs-8 mb-0">
                                            <input type="checkbox" className="form-check-input" />
                                        </div>
                                    ) : (
                                        col.name || ""
                                    )}
                                </th>
                            ) : null
                        )}
                    </tr>
                    </thead>

                    <tbody>
                    {data.length === 0 ? (
                        <tr>
                            <td
                                colSpan={visibleColumns.length}
                                className="text-center py-4 fs-8-1 text-muted fw-semibold"
                            >
                                No record found
                            </td>
                        </tr>
                    ) : (
                        data.map((row, rIndex) => (
                            <tr key={rIndex} className={`${rIndex % 2 === 0 ? "old_row" : ""}`}>
                                {orderedColumns.map((col, cIndex) => {
                                    if (!visibleColumns.includes(col.key)) return null;

                                    const cellId = `${rIndex}-${col.key}`;
                                    const content = col.template
                                        ? col.template(row)
                                        : row[col.key];

                                    const isExpanded = expandedCells[cellId];
                                    const shouldTruncate =
                                        col.isMoreLess &&
                                        typeof content === "string" &&
                                        content.length > 100;

                                    const displayContent =
                                        shouldTruncate && !isExpanded
                                            ? content.slice(0, 100) + "..."
                                            : content;

                                    return (
                                        <td
                                            key={cIndex}
                                            className={`align-middle p-2 ${col.className || ""}`}
                                        >
                                            {displayContent}
                                            {shouldTruncate && (
                                                <a
                                                    href="#"
                                                    className="ms-2 text-primary"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        toggleCell(rIndex, col.key);
                                                    }}
                                                >
                                                    {isExpanded ? "less" : "more"}
                                                </a>
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))
                    )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default Table;
